import {formatDate} from '@angular/common';
import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {
  AdminDashboardCompanyCommercial,
  AdminDashboardCompanyLicence,
  AdminDashboardCompanyTransactions
} from '@features/admin-panel/models/admin-dashboard.models';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {adbUsdFull} from '@features/admin-panel/helpers/admin-dashboard-format';

/** Licence status pill tone (LicenceStatusRule): Active green, Upcoming amber, Expired red, anything else neutral. */
const STATUS_TONE: {[status: string]: string} = {Active: 'green', Upcoming: 'amber', Expired: 'red'};

/**
 * Company 360 "Commercial": the licences with their status chips and ARR (APClientManager), and the last 12 months of
 * billed transactions with their total (APBilling). Each half loads and shows only when its feature is granted.
 * ITP-E-167-03: with the R5 response (ITP-E-165-02 package names, ITP-E-167-02 contract facts) the Licences card lists
 * Package | Price | From | Ends | Status; an older response keeps the R4 columns.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-c360-commercial',
  templateUrl: './admin-dashboard-c360-commercial.component.html',
  styleUrls: ['./admin-dashboard-c360-commercial.component.scss']
})
export class AdminDashboardC360CommercialComponent implements OnChanges, OnDestroy {
  @Input() companyId: string | null = null;
  @Input() licencesGranted = false;
  @Input() billingGranted = false;

  commercial: AdminDashboardCompanyCommercial | null = null;
  transactions: AdminDashboardCompanyTransactions | null = null;

  private commercialSubscription: Subscription;
  private transactionsSubscription: Subscription;

  /** The clock "in N days" counts from (a spec replaces it). */
  now: () => Date = () => new Date();

  constructor(private dataService: AdminDashboardDataService) {}

  /** The Contract row: "Annual, from 1 Nov 2025", or "None" without a current term (ITP-E-167-02 ContractStart). */
  get contractText(): string {
    const start = this.commercial?.ContractStart;
    return this.hasDate(start) ? `Annual, from ${formatDate(start, 'd MMM y', 'en-US', 'UTC')}` : 'None';
  }

  /** The Next renewal row: "31 Oct 2026 · in 26 days" (whole UTC days from today, never below 0), or "—". */
  get nextRenewalText(): string {
    const next = this.commercial?.NextRenewal;
    if (!this.hasDate(next)) {
      return '—';
    }
    const now = this.now();
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const days = Math.max(0, Math.round((Date.parse(next) - today) / 86400000));
    return `${formatDate(next, 'd MMM y', 'en-US', 'UTC')} · in ${countOf(days, 'day')}`;
  }

  /** The Account owner row (Plan ITP-P-35 Q8): the owner, "Unassigned", or "Not in Client Manager" without a record. */
  get ownerText(): string {
    if (this.commercial?.NotApplicable) {
      return 'Not in Client Manager';
    }
    return this.commercial?.OwnerName || 'Unassigned';
  }

  /** True when the response carries the R5 contract facts (ITP-E-167-02); an older API keeps the R4 cards. */
  get hasContractFacts(): boolean {
    return this.commercial?.HasClientManagerRecord !== undefined;
  }

  /** The Renewal history card shows with APClientManager when the response carries one (ITP-E-167-01; empty counts). */
  get showRenewals(): boolean {
    return this.licencesGranted && Array.isArray(this.commercial?.RenewalHistory);
  }

  /** The R5 Licences subtitle: "3 licence lines, USD a year." / "1 licence line, USD a year." / "No paid licences." */
  get licencesSummary(): string {
    const count = this.commercial?.Licences?.length ?? 0;
    return count ? `${countOf(count, 'licence line')}, USD a year.` : 'No paid licences.';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] || changes['licencesGranted']) {
      this.loadCommercial();
    }
    if (changes['companyId'] || changes['billingGranted']) {
      this.loadTransactions();
    }
  }

  ngOnDestroy(): void {
    this.commercialSubscription?.unsubscribe();
    this.transactionsSubscription?.unsubscribe();
  }

  /** A licence with no date set comes back as 0001-01-01; an older API sends no start date at all. */
  hasDate(date: string | null | undefined): boolean {
    return !!date && !date.startsWith('0001-01-01');
  }

  /** The status pill's tone (`data-tone`), see STATUS_TONE. */
  statusTone(status: string): string {
    return STATUS_TONE[status] ?? 'neutral';
  }

  /** The Package cell: the package name (Q11), else the product code, else a dash. */
  packageOf(licence: AdminDashboardCompanyLicence): string {
    return licence.packageName || licence.productCode || '—';
  }

  /** The annual price in whole dollars: "$12,000". */
  usd(value: number): string {
    return adbUsdFull(value);
  }

  private loadCommercial(): void {
    this.commercialSubscription?.unsubscribe();
    this.commercial = null;
    if (!this.companyId || !this.licencesGranted) {
      return;
    }
    this.commercialSubscription = this.dataService.getCompanyCommercial(this.companyId).pipe(
      catchError(() => of(null as AdminDashboardCompanyCommercial))
    ).subscribe(commercial => this.commercial = commercial);
  }

  private loadTransactions(): void {
    this.transactionsSubscription?.unsubscribe();
    this.transactions = null;
    if (!this.companyId || !this.billingGranted) {
      return;
    }
    this.transactionsSubscription = this.dataService.getCompanyTransactions(this.companyId).pipe(
      catchError(() => of(null as AdminDashboardCompanyTransactions))
    ).subscribe(transactions => this.transactions = transactions);
  }
}
